import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CogIcon,
  ArrowPathIcon,
  CheckIcon,
  KeyIcon,
  ShieldCheckIcon,
  BellIcon,
  CurrencyRupeeIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  PhotoIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import { adminSettingsApi } from '../../services/api'
import { HeroSettings } from '../../types'
import analystPhoto from '../../assets/rahul-das.png'
import { resolveMediaUrl } from '../../utils/media'
import { createDefaultHeroSettings } from '../../utils/heroSettings'

interface SystemSettings {
  general: {
    siteName: string
    siteDescription: string
    contactEmail: string
    supportPhone: string
    timezone: string
    currency: string
    language: string
  }
  email: {
    smtpHost: string
    smtpPort: number
    smtpUsername: string
    smtpPassword: string
    fromEmail: string
    fromName: string
  }
  notifications: {
    emailEnabled: boolean
    smsEnabled: boolean
    pushEnabled: boolean
    whatsappEnabled: boolean
  }
  payments: {
    razorpayKeyId: string
    razorpayKeySecret: string
    testMode: boolean
    webhookSecret: string
  }
  security: {
    sessionTimeout: number
    maxLoginAttempts: number
    passwordMinLength: number
    requireTwoFactor: boolean
    apiRateLimit: number
  }
  features: {
    maintenanceMode: boolean
    userRegistration: boolean
    emailVerification: boolean
    kycRequired: boolean
  }
}

const DEFAULT_SETTINGS: SystemSettings = {
  general: {
    siteName: 'AnalyzerView',
    siteDescription: 'Professional Stock Research & Analysis Platform',
    contactEmail: 'contact@analyzerview.com',
    supportPhone: '+91-9876543210',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    language: 'en'
  },
  email: {
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: 'noreply@analyzerview.com',
    fromName: 'AnalyzerView Team'
  },
  notifications: {
    emailEnabled: true,
    smsEnabled: false,
    pushEnabled: false,
    whatsappEnabled: false
  },
  payments: {
    razorpayKeyId: '',
    razorpayKeySecret: '',
    testMode: false,
    webhookSecret: ''
  },
  security: {
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireTwoFactor: false,
    apiRateLimit: 100
  },
  features: {
    maintenanceMode: false,
    userRegistration: true,
    emailVerification: true,
    kycRequired: true
  }
}

const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('general')
  const [savedMessage, setSavedMessage] = useState('')
  const [heroSettings, setHeroSettings] = useState<HeroSettings>(createDefaultHeroSettings())
  const [heroUpdatedAt, setHeroUpdatedAt] = useState<string | null>(null)
  const [selectedProfileImage, setSelectedProfileImage] = useState<File | null>(null)
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string | null>(null)
  const [heroLoading, setHeroLoading] = useState(false)
  const [heroSaving, setHeroSaving] = useState(false)
  const [heroRestoring, setHeroRestoring] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [heroError, setHeroError] = useState('')
  const [profileImageFailed, setProfileImageFailed] = useState(false)
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({})
  const profileFileInputRef = useRef<HTMLInputElement | null>(null)

  const currentProfileImage = useMemo(() => {
    if (profilePreviewUrl) return profilePreviewUrl
    const resolved = resolveMediaUrl(heroSettings.profile_image_url)
    if (resolved) return resolved
    return analystPhoto
  }, [profilePreviewUrl, heroSettings.profile_image_url])

  const isDefaultProfileImage = !heroSettings.profile_image_url

  useEffect(() => {
    void loadHeroSettings()
  }, [])

  useEffect(() => {
    return () => {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl)
    }
  }, [profilePreviewUrl])

  useEffect(() => {
    setProfileImageFailed(false)
  }, [currentProfileImage])

  const loadHeroSettings = async () => {
    setHeroLoading(true)
    setHeroError('')
    try {
      const res = await adminSettingsApi.getHeroSettings()
      setHeroSettings(res.hero_settings)
      setHeroUpdatedAt(res.updated_at)
    } catch (e: any) {
      const message = e.response?.data?.detail || 'Failed to load hero settings.'
      setHeroError(message)
      toast.error(message)
    } finally {
      setHeroLoading(false)
    }
  }

  const validateHeroFile = (file: File): string | null => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!allowed.includes(file.type)) {
      return 'Only JPG, JPEG, PNG, and WebP files are allowed.'
    }
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      return 'Maximum allowed file size is 5 MB.'
    }
    return null
  }

  const updateHeroField = (key: keyof HeroSettings, value: string) => {
    setHeroSettings(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  const updateFeatureCard = (index: number, key: 'title' | 'subtitle', value: string) => {
    setHeroSettings(prev => ({
      ...prev,
      feature_cards: prev.feature_cards.map((card, cardIndex) => (
        cardIndex === index ? { ...card, [key]: value } : card
      )),
    }))
  }

  const updateStatistic = (index: number, key: 'value' | 'label', value: string) => {
    setHeroSettings(prev => ({
      ...prev,
      analyst_statistics: prev.analyst_statistics.map((stat, statIndex) => (
        statIndex === index ? { ...stat, [key]: value } : stat
      )),
    }))
  }

  const updateTrustItem = (index: number, key: 'title' | 'subtitle', value: string) => {
    setHeroSettings(prev => ({
      ...prev,
      trust_items: prev.trust_items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      )),
    }))
  }

  const handleSelectImage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validationError = validateHeroFile(file)
    if (validationError) {
      setHeroError(validationError)
      toast.error(validationError)
      event.target.value = ''
      return
    }

    const nextPreview = URL.createObjectURL(file)
    if (profilePreviewUrl) {
      URL.revokeObjectURL(profilePreviewUrl)
    }
    setSelectedProfileImage(file)
    setProfilePreviewUrl(nextPreview)
    setHeroError('')
    setUploadProgress(0)
  }

  const handleSaveHeroSettings = async () => {
    setHeroSaving(true)
    setHeroError('')
    setUploadProgress(0)

    try {
      const res = await adminSettingsApi.updateHeroSettings(
        heroSettings,
        {
          profileImageFile: selectedProfileImage,
        },
        setUploadProgress,
      )
      setHeroSettings(res.hero_settings)
      setHeroUpdatedAt(res.updated_at)
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl)
      setProfilePreviewUrl(null)
      setSelectedProfileImage(null)
      if (profileFileInputRef.current) profileFileInputRef.current.value = ''
      toast.success('Hero settings updated successfully.')
      setSavedMessage('Hero settings saved successfully')
      setTimeout(() => setSavedMessage(''), 3000)
      window.dispatchEvent(new Event('hero-settings-updated'))
      window.dispatchEvent(new Event('hero-image-updated'))
    } catch (e: any) {
      const message = e.response?.data?.detail || 'Failed to save hero settings.'
      setHeroError(message)
      toast.error(message)
    } finally {
      setHeroSaving(false)
      setUploadProgress(0)
    }
  }

  const handleRestoreHeroSettings = async () => {
    const confirmed = window.confirm('Restore Hero Section settings to defaults? This will reset text, cards, stats, trust bar, and profile image.')
    if (!confirmed) return

    setHeroRestoring(true)
    setHeroError('')
    try {
      const res = await adminSettingsApi.restoreHeroSettings()
      setHeroSettings(res.hero_settings)
      setHeroUpdatedAt(res.updated_at)
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl)
      setProfilePreviewUrl(null)
      setSelectedProfileImage(null)
      if (profileFileInputRef.current) profileFileInputRef.current.value = ''
      toast.success('Hero settings restored to default.')
      setSavedMessage('Hero settings restored to default')
      setTimeout(() => setSavedMessage(''), 3000)
      window.dispatchEvent(new Event('hero-settings-updated'))
      window.dispatchEvent(new Event('hero-image-updated'))
    } catch (e: any) {
      const message = e.response?.data?.detail || 'Failed to restore hero settings.'
      setHeroError(message)
      toast.error(message)
    } finally {
      setHeroRestoring(false)
    }
  }

  const validateEmailSettings = (email: SystemSettings['email']) => {
    const errs: Record<string, string> = {}
    if (!email.smtpHost.trim()) errs.smtpHost = 'SMTP host is required.'
    if (!Number.isInteger(email.smtpPort) || email.smtpPort < 1 || email.smtpPort > 65535) {
      errs.smtpPort = 'Port must be between 1 and 65535.'
    }
    if (!email.smtpUsername.trim()) errs.smtpUsername = 'SMTP username is required.'
    if (!email.smtpPassword.trim()) errs.smtpPassword = 'SMTP password is required.'
    if (!email.fromEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.fromEmail.trim())) {
      errs.fromEmail = 'Please enter a valid email address.'
    }
    if (!email.fromName.trim()) errs.fromName = 'From name is required.'
    return errs
  }

  const handleSave = async (section: keyof SystemSettings) => {
    if (section === 'email') {
      const errs = validateEmailSettings(settings.email)
      setEmailErrors(errs)
      if (Object.keys(errs).length > 0) return
    }
    setSaving(true)
    await new Promise(r => setTimeout(r, 300))
    setSavedMessage(`${section} settings saved locally`)
    setTimeout(() => setSavedMessage(''), 3000)
    setSaving(false)
  }

  const updateSetting = (section: keyof SystemSettings, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }))
  }

  const tabs = [
    { id: 'hero', name: 'Hero Section', icon: PhotoIcon },
    { id: 'general', name: 'General', icon: CogIcon },
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'payments', name: 'Payments', icon: CurrencyRupeeIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'features', name: 'Features', icon: GlobeAltIcon }
  ]

  const activeSystemTab = activeTab === 'hero' ? null : (activeTab as keyof SystemSettings)
  const activeTabName = tabs.find((tab) => tab.id === activeTab)?.name ?? 'Settings'

  const handleSaveActiveTab = () => {
    if (activeTab === 'hero') {
      void handleSaveHeroSettings()
      return
    }

    if (activeSystemTab) {
      void handleSave(activeSystemTab)
    }
  }

  const handleRestoreActiveTab = () => {
    if (activeTab === 'hero') {
      void handleRestoreHeroSettings()
    }
  }

  const handleRefreshActiveTab = () => {
    if (activeTab === 'hero') {
      void loadHeroSettings()
    }
  }

  return (
    <div className="space-y-5 pb-28">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-white">System Settings</h1>
          <p className="text-gray-400 mt-1">Configure system-wide settings and preferences</p>
        </div>

        <Card className="p-2 sm:p-3">
          <div className="overflow-x-auto">
            <nav className="flex min-w-max items-center gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-500/10 text-blue-400'
                        : 'text-gray-400 hover:bg-dark-700 hover:text-gray-300'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${activeTab === tab.id ? 'text-blue-400' : 'text-gray-400'}`} />
                    <span className="whitespace-nowrap">{tab.name}</span>
                  </button>
                )
              })}
            </nav>
          </div>
        </Card>
      </div>

      {/* Success Message */}
      {savedMessage && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded">
          <div className="flex items-center">
            <CheckIcon className="w-5 h-5 mr-2" />
            {savedMessage}
          </div>
        </div>
      )}

      <div className="min-w-0 w-full">
        {/* Content Area */}
        <div className="min-w-0 w-full">
          {/* Hero Section Settings */}
          {activeTab === 'hero' && (
            <Card className="p-4 sm:p-5">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-white">Hero Section</h3>
                  <p className="mt-1 text-sm text-gray-400">Manage homepage copy, cards, statistics, trust bar, and imagery.</p>
                </div>
                {heroUpdatedAt && <span className="text-xs text-gray-500">Updated {new Date(heroUpdatedAt).toLocaleString('en-IN')}</span>}
              </div>
              <div className="space-y-4">

                {heroError && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded">
                    {heroError}
                  </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.5fr)_minmax(280px,.75fr)] gap-4">
                  <div className="rounded-xl border border-dark-700 bg-dark-900/40 p-3 sm:p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-base font-semibold text-white">Hero Content</h4>
                      <span className="text-xs text-gray-500">Homepage copy</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Main Heading</label>
                      <Input type="text" value={heroSettings.main_heading} onChange={(e) => updateHeroField('main_heading', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Highlight Text 1</label>
                        <Input type="text" value={heroSettings.highlight_text_1} onChange={(e) => updateHeroField('highlight_text_1', e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Highlight Text 2</label>
                        <Input type="text" value={heroSettings.highlight_text_2} onChange={(e) => updateHeroField('highlight_text_2', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Hero Description</label>
                      <textarea value={heroSettings.hero_description} onChange={(e) => updateHeroField('hero_description', e.target.value)} rows={4} className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                  </div>
                  <div className="rounded-xl border border-dark-700 bg-dark-900/50 p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Profile Image</p>
                    <div className="relative overflow-hidden rounded-xl border border-dark-700 bg-dark-950/40">
                      <img
                        src={profileImageFailed ? analystPhoto : currentProfileImage}
                        alt="Profile preview"
                        className="w-full max-h-80 object-cover"
                        onError={() => setProfileImageFailed(true)}
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-400">
                      <span>{isDefaultProfileImage ? 'Using default profile image' : 'Custom profile image active'}</span>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => profileFileInputRef.current?.click()}
                        disabled={heroSaving || heroLoading || heroRestoring}
                      >
                        <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                        Upload Profile Image
                      </Button>
                    </div>
                  </div>
                </div>
                <input
                    ref={profileFileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleSelectImage(e)}
                    className="hidden"
                    disabled={heroSaving || heroLoading || heroRestoring}
                />

                  <div className="rounded-xl border border-dark-700 bg-dark-900/40 p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h4 className="text-base font-semibold text-white">Left Feature Cards</h4>
                        <p className="text-xs text-gray-400">Exactly 4 cards. Icons and order stay fixed.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {heroSettings.feature_cards.map((card, index) => (
                        <div key={`feature-card-${index}`} className="rounded-lg border border-dark-700 bg-dark-950/40 p-3 space-y-2">
                          <p className="text-sm font-medium text-white">Card {index + 1}</p>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Card Title</label>
                            <Input
                              type="text"
                              value={card.title}
                              onChange={(e) => updateFeatureCard(index, 'title', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Card Subtitle</label>
                            <Input
                              type="text"
                              value={card.subtitle}
                              onChange={(e) => updateFeatureCard(index, 'subtitle', e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-dark-700 bg-dark-900/40 p-3 sm:p-4 space-y-3">
                    <div>
                      <h4 className="text-base font-semibold text-white">Analyst Statistics</h4>
                      <p className="text-xs text-gray-400">Values and labels are editable. Count and order stay fixed.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {heroSettings.analyst_statistics.map((stat, index) => (
                        <div key={`analyst-stat-${index}`} className="rounded-lg border border-dark-700 bg-dark-950/40 p-3 space-y-2">
                          <p className="text-sm font-medium text-white">Statistic {index + 1}</p>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Value</label>
                            <Input
                              type="text"
                              value={stat.value}
                              onChange={(e) => updateStatistic(index, 'value', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Label</label>
                            <Input
                              type="text"
                              value={stat.label}
                              onChange={(e) => updateStatistic(index, 'label', e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-dark-700 bg-dark-900/40 p-3 sm:p-4 space-y-3">
                    <div>
                      <h4 className="text-base font-semibold text-white">Bottom Trust Bar</h4>
                      <p className="text-xs text-gray-400">Exactly 5 items. Icons and order stay fixed.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {heroSettings.trust_items.map((item, index) => (
                        <div key={`trust-item-${index}`} className="rounded-lg border border-dark-700 bg-dark-950/40 p-3 space-y-2">
                          <p className="text-sm font-medium text-white">Trust Item {index + 1}</p>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                            <Input
                              type="text"
                              value={item.title}
                              onChange={(e) => updateTrustItem(index, 'title', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Subtitle</label>
                            <Input
                              type="text"
                              value={item.subtitle}
                              onChange={(e) => updateTrustItem(index, 'subtitle', e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedProfileImage && <p className="text-sm text-gray-300">Profile image selected: <span className="font-medium">{selectedProfileImage.name}</span></p>}

                  {(heroSaving || uploadProgress > 0) && (
                    <div className="space-y-2">
                      <div className="h-2 w-full rounded-full bg-dark-700 overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400">Uploading... {uploadProgress}%</p>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    Allowed formats: JPG, JPEG, PNG, WebP. Maximum size: 5 MB. CTA buttons remain hardcoded on the homepage.
                  </p>
                </div>
            </Card>
          )}

          {/* General Settings */}
          {activeTab === 'general' && (
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">General Settings</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Site Name</label>
                    <Input
                      type="text"
                      value={settings.general.siteName}
                      onChange={(e) => updateSetting('general', 'siteName', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Contact Email</label>
                    <Input
                      type="email"
                      value={settings.general.contactEmail}
                      onChange={(e) => updateSetting('general', 'contactEmail', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Site Description</label>
                  <textarea
                    value={settings.general.siteDescription}
                    onChange={(e) => updateSetting('general', 'siteDescription', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Support Phone</label>
                    <Input
                      type="text"
                      value={settings.general.supportPhone}
                      onChange={(e) => updateSetting('general', 'supportPhone', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Timezone</label>
                    <select
                      value={settings.general.timezone}
                      onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New_York</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Currency</label>
                    <select
                      value={settings.general.currency}
                      onChange={(e) => updateSetting('general', 'currency', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-900 border border-dark-600 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Email Settings</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">SMTP Host</label>
                    <Input
                      type="text"
                      value={settings.email.smtpHost}
                      onChange={(e) => updateSetting('email', 'smtpHost', e.target.value)}
                      error={emailErrors.smtpHost}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">SMTP Port</label>
                    <Input
                      type="number"
                      value={settings.email.smtpPort}
                      onChange={(e) => updateSetting('email', 'smtpPort', parseInt(e.target.value))}
                      error={emailErrors.smtpPort}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">SMTP Username</label>
                    <Input
                      type="text"
                      value={settings.email.smtpUsername}
                      onChange={(e) => updateSetting('email', 'smtpUsername', e.target.value)}
                      error={emailErrors.smtpUsername}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">SMTP Password</label>
                    <div className="relative">
                      <Input
                        type="password"
                        value={settings.email.smtpPassword}
                        onChange={(e) => updateSetting('email', 'smtpPassword', e.target.value)}
                        error={emailErrors.smtpPassword}
                      />
                      <KeyIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">From Email</label>
                    <Input
                      type="email"
                      value={settings.email.fromEmail}
                      onChange={(e) => updateSetting('email', 'fromEmail', e.target.value)}
                      error={emailErrors.fromEmail}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">From Name</label>
                    <Input
                      type="text"
                      value={settings.email.fromName}
                      onChange={(e) => updateSetting('email', 'fromName', e.target.value)}
                      error={emailErrors.fromName}
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Notification Settings</h3>
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">Email Notifications</label>
                      <p className="text-xs text-gray-400">Send notifications via email</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.emailEnabled}
                      onChange={(e) => updateSetting('notifications', 'emailEnabled', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">SMS Notifications</label>
                      <p className="text-xs text-gray-400">Send notifications via SMS</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.smsEnabled}
                      onChange={(e) => updateSetting('notifications', 'smsEnabled', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">Push Notifications</label>
                      <p className="text-xs text-gray-400">Send push notifications to mobile apps</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.pushEnabled}
                      onChange={(e) => updateSetting('notifications', 'pushEnabled', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">WhatsApp Notifications</label>
                      <p className="text-xs text-gray-400">Send notifications via WhatsApp</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.whatsappEnabled}
                      onChange={(e) => updateSetting('notifications', 'whatsappEnabled', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Payment Settings */}
          {activeTab === 'payments' && (
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Payment Settings</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Razorpay Key ID</label>
                    <div className="relative">
                      <Input
                        type="text"
                        value={settings.payments.razorpayKeyId}
                        onChange={(e) => updateSetting('payments', 'razorpayKeyId', e.target.value)}
                      />
                      <KeyIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Razorpay Key Secret</label>
                    <div className="relative">
                      <Input
                        type="password"
                        value={settings.payments.razorpayKeySecret}
                        onChange={(e) => updateSetting('payments', 'razorpayKeySecret', e.target.value)}
                      />
                      <KeyIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Webhook Secret</label>
                  <div className="relative">
                    <Input
                      type="password"
                      value={settings.payments.webhookSecret}
                      onChange={(e) => updateSetting('payments', 'webhookSecret', e.target.value)}
                    />
                    <KeyIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Test Mode</label>
                    <p className="text-xs text-gray-400">Enable test mode for payments</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.payments.testMode}
                    onChange={(e) => updateSetting('payments', 'testMode', e.target.checked)}
                    className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Security Settings</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Session Timeout (minutes)</label>
                    <Input
                      type="number"
                      value={settings.security.sessionTimeout}
                      onChange={(e) => updateSetting('security', 'sessionTimeout', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Max Login Attempts</label>
                    <Input
                      type="number"
                      value={settings.security.maxLoginAttempts}
                      onChange={(e) => updateSetting('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Password Min Length</label>
                    <Input
                      type="number"
                      value={settings.security.passwordMinLength}
                      onChange={(e) => updateSetting('security', 'passwordMinLength', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">API Rate Limit (requests/minute)</label>
                  <Input
                    type="number"
                    value={settings.security.apiRateLimit}
                    onChange={(e) => updateSetting('security', 'apiRateLimit', parseInt(e.target.value))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Require Two-Factor Authentication</label>
                    <p className="text-xs text-gray-400">Force 2FA for all admin accounts</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.security.requireTwoFactor}
                    onChange={(e) => updateSetting('security', 'requireTwoFactor', e.target.checked)}
                    className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Feature Settings */}
          {activeTab === 'features' && (
            <Card>
              <h3 className="text-lg font-semibold text-white mb-4">Feature Settings</h3>
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">Maintenance Mode</label>
                      <p className="text-xs text-gray-400">Put the site in maintenance mode</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.features.maintenanceMode}
                      onChange={(e) => updateSetting('features', 'maintenanceMode', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">User Registration</label>
                      <p className="text-xs text-gray-400">Allow new user registrations</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.features.userRegistration}
                      onChange={(e) => updateSetting('features', 'userRegistration', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">Email Verification</label>
                      <p className="text-xs text-gray-400">Require email verification for new users</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.features.emailVerification}
                      onChange={(e) => updateSetting('features', 'emailVerification', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-300">KYC Required</label>
                      <p className="text-xs text-gray-400">Require KYC verification for premium features</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.features.kycRequired}
                      onChange={(e) => updateSetting('features', 'kycRequired', e.target.checked)}
                      className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-dark-600 rounded"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <div className="fixed bottom-4 right-4 z-30">
        <div className="flex flex-wrap items-center justify-end gap-2 rounded-xl border border-dark-700 bg-dark-900/95 p-3 shadow-lg backdrop-blur">
          <Button
            type="button"
            onClick={handleSaveActiveTab}
            loading={activeTab === 'hero' ? heroSaving : saving}
            disabled={activeTab === 'hero' ? (heroLoading || heroRestoring) : false}
          >
            Save {activeTabName}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleRestoreActiveTab}
            loading={heroRestoring}
            disabled={activeTab !== 'hero' || heroSaving || heroLoading}
          >
            Restore to Default
          </Button>

          <Button
            type="button"
            variant="secondary"
            onClick={handleRefreshActiveTab}
            loading={heroLoading}
            disabled={activeTab !== 'hero' || heroSaving || heroRestoring}
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AdminSettings
